/**
 * Step 2 — Listing details (BR-1, BR-1a, BR-1b, BR-107). The first save creates the draft Listing
 * (asking rent, available date and accepted lease terms all exist by then), every later save updates
 * it. The fields and the form state are the ones the live Listing editor uses
 * (ListingTermsFields + useListingTermsForm). Jurisdiction warnings are soft: they show inline and in
 * the header note and never block a save; BR-1b server validation errors surface inline.
 *
 * Test ids keep the `create-listing-` prefix the Create Listing form used, so the same fields keep
 * the same ids.
 */

import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import { Skeleton } from '../../../../components/ui/skeleton';
import {
  createListing,
  updateListing,
  type ConstraintWarning,
  type Listing,
} from '../../../../lib/leasing.api';
import { ListingTermsFields } from '../../listing-detail/ListingTermsFields';
import { WarningBanner, useUnitConstraints } from '../../listing-detail/shared';
import {
  NEW_LISTING_TERMS,
  countTermsWarnings,
  termsValuesFromListing,
  useListingTermsForm,
} from '../../listing-detail/useListingTermsForm';
import type { GuideStepHandle, GuideStepProps } from '../guide-steps';

const ID_PREFIX = 'create';
const TEST_ID_PREFIX = 'create-listing-';

function DetailsSkeleton() {
  return (
    <div className="space-y-5" data-testid="listing-details-loading" aria-busy="true">
      {[24, 24, 56, 32, 40].map((height, index) => (
        <Skeleton key={index} className="w-full" style={{ height: `${height * 4}px` }} />
      ))}
    </div>
  );
}

function saveErrorMessage(err: unknown): string {
  if ((err as { status?: number } | null)?.status === 409) {
    return 'A listing for this unit is already active or paused. Close it before creating a new one.';
  }
  return err instanceof Error ? err.message : 'Failed to save listing details';
}

export function ListingDetailsStep({
  unitId,
  listing,
  handleRef,
  onDirtyChange,
  onSavingChange,
  onListingCreated,
}: GuideStepProps) {
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const { constraints, isLoading: constraintsLoading, isError: constraintsError, refetch } = useUnitConstraints(unitId);
  const form = useListingTermsForm(listing ? termsValuesFromListing(listing) : NEW_LISTING_TERMS, {
    hadIncomeRestrictions: listing?.incomeRestrictions != null,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [serverWarnings, setServerWarnings] = useState<ConstraintWarning[]>([]);
  // The first save's re-entrancy guard. `saving` reaches the shell one render late and the `listing`
  // prop only updates after the route change, so a second save (Save and exit then Save and continue,
  // or the leave prompt's Save) can start before either is visible: it joins the first save instead of
  // POSTing a second create (a 409), and a save after it finished updates the listing just created.
  const createInFlightRef = useRef<Promise<boolean> | null>(null);
  const createdListingRef = useRef<Listing | null>(null);

  useEffect(() => onDirtyChange(form.dirty), [form.dirty, onDirtyChange]);
  useEffect(() => onSavingChange(saving), [saving, onSavingChange]);

  useImperativeHandle(
    handleRef,
    (): GuideStepHandle => ({
      isDirty: () => form.dirty,
      save: async () => {
        if (createInFlightRef.current) return createInFlightRef.current;
        const saved = listing ?? createdListingRef.current;
        // A saved, unedited Listing has nothing to send.
        if (saved && !form.dirty) return true;
        setError(null);
        setServerWarnings([]);
        if (!form.validate()) {
          // Move to the first invalid field once the errors have rendered.
          requestAnimationFrame(() =>
            containerRef.current?.querySelector<HTMLElement>('[aria-invalid="true"], [role="alert"]')?.focus()
          );
          return false;
        }
        const run = async (): Promise<boolean> => {
          setSaving(true);
          try {
            const result = saved
              ? await updateListing(saved.id, form.toUpdateBody())
              : await createListing(form.toCreateBody(unitId));
            form.markSaved();
            setServerWarnings(result.warnings);
            void queryClient.invalidateQueries({ queryKey: ['listing', result.listing.id] });
            void queryClient.invalidateQueries({ queryKey: ['listings'] });
            if (!saved) {
              createdListingRef.current = result.listing;
              onListingCreated?.(result.listing);
            }
            return true;
          } catch (err) {
            setError(saveErrorMessage(err));
            return false;
          } finally {
            setSaving(false);
          }
        };
        if (saved) return run();
        const created = run().finally(() => {
          createInFlightRef.current = null;
        });
        createInFlightRef.current = created;
        return created;
      },
    }),
    [form, listing, unitId, queryClient, onListingCreated]
  );

  const warningCount = countTermsWarnings(form.values, constraints) + serverWarnings.length;

  return (
    <div data-testid="guide-step-details">
      <div ref={containerRef} data-testid="listing-details-step" className="space-y-5">
        <header>
          <h1 className="text-2xl font-semibold" data-testid="guide-step-heading">
            <span data-testid="listing-details-heading">Listing details</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            The money comes first, then your policies. Prospects see all of this on the listing.
          </p>
          {warningCount > 0 && (
            <p className="text-sm text-warning mt-2" data-testid="listing-details-warning-note">
              {warningCount} jurisdiction warning{warningCount > 1 ? 's' : ''} to review. You can still save and continue.
            </p>
          )}
        </header>

        {constraintsError && (
          <div
            role="alert"
            data-testid="listing-details-constraints-error"
            className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
          >
            <span>Local rules could not be loaded, so limits and warnings are hidden. You can still fill this in.</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="min-h-[44px]"
              onClick={() => void refetch()}
              data-testid="listing-details-constraints-retry"
            >
              Retry
            </Button>
          </div>
        )}

        {serverWarnings.length > 0 && <WarningBanner warnings={serverWarnings} />}

        {constraintsLoading ? (
          <DetailsSkeleton />
        ) : (
          <form
            onSubmit={(e) => e.preventDefault()}
            noValidate
            className="space-y-5"
            data-testid="create-listing-form"
            aria-label="Listing details"
          >
            <ListingTermsFields form={form} constraints={constraints} idPrefix={ID_PREFIX} testIdPrefix={TEST_ID_PREFIX} />
          </form>
        )}

        {error && (
          <div
            role="alert"
            data-testid="create-listing-error"
            className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
